import { useState, type ComponentType } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Store,
  Mountain,
  Factory,
  Truck,
  Zap,
  HardHat,
  HeartPulse,
  Briefcase,
  Landmark,
} from "lucide-react";
import teamAssetSrc from "@/assets/team.png";
const teamAsset = { url: teamAssetSrc };
import { gapContainer, gapItem, gapItemStatic, gapViewport } from "@/components/site/gap-motion";

type IconType = ComponentType<{ className?: string; "aria-hidden"?: boolean | "true" | "false" }>;

const industryIcons: Record<string, IconType> = {
  "small-midsize-businesses": Store,
  "mining-natural-resources": Mountain,
  manufacturing: Factory,
  "supply-chain-logistics": Truck,
  "utilities-energy": Zap,
  "construction-engineering": HardHat,
  healthcare: HeartPulse,
  "professional-business-services": Briefcase,
  "government-public-sector": Landmark,
};

import { courses, featuredCourses } from "@/content/courses";
import { industries, INDUSTRY_NAMES } from "@/content/industries";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

/**
 * Featured courses marquee.
 *
 * The courses shown here are controlled by `FEATURED_SLUGS` in
 * `src/content/courses.ts`. The client will supply the real featured list
 * later; edit that Set and this carousel updates automatically.
 *
 * Motion notes:
 * - Continuous auto-scroll via the `.lel-marquee-track` animation in styles.css.
 * - Pauses on hover, on keyboard focus, and while a touch drag is in progress.
 * - Under `prefers-reduced-motion` the animation is off and the viewport is a
 *   plain horizontal scroll container.
 */
export function FeaturedCoursesCarousel() {
  const [paused, setPaused] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const item = reduceMotion ? gapItemStatic : gapItem;
  const items = featuredCourses;
  if (items.length === 0) return null;

  // Duplicated once so the track can loop seamlessly at -50%.
  const loop = [...items, ...items];

  return (
    <section
      className="border-y border-border/60 py-14 md:py-16"
      style={{
        backgroundImage:
          "linear-gradient(160deg, #EFECF4 0%, #DFD8E9 42%, #EAE5F1 68%, var(--background) 100%)",
      }}
    >
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={gapViewport}
        variants={gapContainer}
      >
        <motion.div
          variants={item}
          className="mx-auto mb-8 flex max-w-6xl flex-wrap items-baseline justify-between gap-x-6 gap-y-3 px-6"
        >
          <div>
            <h2
              className="text-3xl font-semibold leading-tight text-ink md:text-4xl"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Start where your managers need it the most
            </h2>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:gap-6">
            <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
              <DialogTrigger className="text-left text-sm font-semibold text-primary transition-colors hover:text-primary/80 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background">
                Learning Paths →
              </DialogTrigger>
              <DialogContent className="max-w-5xl rounded-[8px]">
                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <DialogHeader>
                      <DialogTitle
                        className="text-xl text-ink"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        Which industry fits you best?
                      </DialogTitle>
                      <DialogDescription style={{ fontFamily: "'Poppins', sans-serif" }}>
                        Pick the closest one and we'll show you where to start.
                      </DialogDescription>
                    </DialogHeader>
                    <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                      {industries.map((industry) => {
                        const Icon = industryIcons[industry.slug];
                        return (
                          <li key={industry.slug}>
                            <button
                              type="button"
                              onClick={() => {
                                setPickerOpen(false);
                                navigate({
                                  to: "/who-we-help/$slug/",
                                  params: { slug: industry.slug },
                                  hash: "learning-path",
                                });
                              }}
                              className="group flex w-full items-center gap-3 rounded-[8px] border border-border/60 bg-background px-4 py-3 text-left transition-colors hover:border-primary/30 hover:bg-lavender focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              {Icon && (
                                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-lavender text-primary transition-colors group-hover:bg-lavender-deep">
                                  <Icon className="h-[1.05rem] w-[1.05rem]" aria-hidden="true" />
                                </span>
                              )}
                              <span
                                className="text-sm font-normal text-ink transition-colors group-hover:text-primary"
                                style={{ fontFamily: "'Poppins', sans-serif" }}
                              >
                                {INDUSTRY_NAMES[industry.slug] ?? industry.eyebrow}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  <div className="mt-2 hidden min-h-[320px] overflow-hidden rounded-[8px] bg-lavender/60 md:mt-8 md:block">
                    <img
                      src={teamAsset.url}
                      alt="A team reviewing leadership development priorities together"
                      className="h-full min-h-[320px] w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <Link
              to="/course-catalog/"
              className="text-sm font-semibold text-primary transition-colors hover:text-primary/80 hover:underline"
            >
              Browse all {courses.length} courses →
            </Link>
          </div>
        </motion.div>

        <motion.div
          variants={item}
          className="lel-marquee-viewport overflow-hidden pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          data-paused={paused ? "true" : "false"}
          onTouchStart={() => setPaused(true)}
          onTouchEnd={() => setPaused(false)}
        >
          <ul className="lel-marquee-track flex w-max gap-5 px-6">
            {loop.map((course, i) => {
              const isClone = i >= items.length;
              return (
                <li key={`${course.slug}-${i}`} aria-hidden={isClone || undefined}>
                  <Link
                    to="/course-catalog/$slug/"
                    params={{ slug: course.slug }}
                    tabIndex={isClone ? -1 : undefined}
                    className="group flex h-full w-[203px] flex-col overflow-hidden rounded-[8px] border border-border/60 bg-background transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_10px_30px_-8px_rgba(0,0,0,0.15)] focus-visible:-translate-y-1 focus-visible:border-primary/40 focus-visible:shadow-[0_10px_30px_-8px_rgba(0,0,0,0.15)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:w-[235px]"
                  >
                    <div className="aspect-square w-full overflow-hidden bg-lavender">
                      {course.image ? (
                        <img
                          src={course.image}
                          alt=""
                          loading="lazy"
                          className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="h-full w-full gradient-brand opacity-80" />
                      )}
                    </div>
                    <div className="flex flex-1 flex-col p-5">
                      <h3
                        className="line-clamp-2 min-h-[2.75em] text-base leading-snug text-ink"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        {course.title}
                      </h3>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {[course.duration].filter(Boolean).map((pill) => (
                          <span
                            key={pill}
                            className="rounded-full border border-primary/20 bg-lavender px-3 py-1 text-xs font-semibold text-primary"
                          >
                            {pill}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </motion.div>
      </motion.div>
    </section>
  );
}
